import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import {
  ColDef,
  ColumnResizedEvent,
  GetRowIdFunc,
  GridApi,
  ColumnApi,
  GridOptions,
  GridReadyEvent,
  IServerSideDatasource,
  FirstDataRenderedEvent, FilterChangedEvent
} from 'ag-grid-community';
import Utils from '@app/utils/utils';

@Component({
  selector: 'app-grid',
  templateUrl: './grid.component.html',
  styleUrls: ['./grid.component.scss']
})
export class GridComponent implements OnChanges {

  // storageKey is used to save the user-resized column widths
  // to local storage
  @Input() public storageKey: string;
  @Input() public userPrefs: any;
  @Input() public isLoading: boolean;
  @Input() public data: any[];
  @Input() public columnDefs: ColDef[];
  @Input() public serverSideDatasource: IServerSideDatasource;
  @Input() public rowSelection = 'single';
  @Input() public gridOptions: GridOptions;
  @Input() public rowIdField: string;
  @Input() public overlayMessage: string;
  @Input() public resetGridFilters: EventEmitter<any>;
  @Output() gridReady: EventEmitter<GridReadyEvent> = new EventEmitter();
  @Output() rowSelected: EventEmitter<any> = new EventEmitter();
  @Output() rowClicked: EventEmitter<any> = new EventEmitter();
  public rowModelType = 'clientSide';
  private gridApi: GridApi;
  private columnApi: ColumnApi;

  public defaultColDef: ColDef = {
    sortable: true,
    filter: true,
    resizable: true,
    menuTabs: ['filterMenuTab'],
    filterParams: {
      suppressAndOrCondition: true,
      filterOptions: ['contains']
    }
  };
  private noRowsHtmlString = '';
  noFilteredRecords = false;
  getRowIdFunc: GetRowIdFunc = (params) => params.data[this.rowIdField];

  async ngOnChanges(changes: any) {
    this.noRowsHtmlString = this.overlayMessage ? 
      Utils.htmlEncode(this.overlayMessage) : '&nbsp;';

    this.rowModelType = changes.serverSideDatasource ? 'serverSide' : null;

    if (changes.userPrefs && !changes.userPrefs.firstChange) {
      this.userPrefs = changes.userPrefs.currentValue;
      // Try to resize the column.
      await this.resizeColumns();
    }
  }

  onRowClicked(event) {
    this.rowClicked.emit(event);
  }

  onRowSelected(event) {
    this.rowSelected.emit({
      data: event.data,
      index: event.rowIndex,
      isChecked: event.node.isSelected()
    });
  }

  async onGridReady(params: GridReadyEvent) {
    this.gridApi = params.api;
    this.columnApi = params.columnApi;

    if (this.rowIdField) {
      params.api.setGetRowId(this.getRowIdFunc);
    }
    // Forward the event to parent listeners
    this.gridReady.emit(params);

    // Reset grid filters if the event was triggered from parent
    if (this.resetGridFilters) {
      this.resetGridFilters.subscribe(e => {
        this.noFilteredRecords = false;
        this.gridApi.setFilterModel(null);
      })
    }
  }

  getContextMenuItems(params) {
    return ['copy'];
  }

  async onColumnResized(e: ColumnResizedEvent) {
    // Only process user resize events that are finished
    if (e.source !== 'uiColumnDragged' || !e.finished) {
      return;
    }
    const col = e.columns[0];
    const key = col.getColId();
    const width = col.getActualWidth();
    const columnWidths = {};
    columnWidths[key] = width;
    await this.userPrefs.saveColumnWidths(columnWidths);
  }

  /**
   * Resize the column. Expects the grid to be ready.
   */
  async resizeColumns() {
    // If grid ain't ready, do nothing.
    if (!this.gridApi || !this.columnApi) {
      return;
    }
    // Set column widths from the saved values
    const columnWidths = await this.userPrefs.getColumnWidths() || {};
    if (!Utils.isObjectEmpty(columnWidths)) {
      const columnWidthsArray = [];
      for (const key of Object.keys(columnWidths)) {
        columnWidthsArray.push({ 'key': key, 'newWidth': columnWidths[key] });
      }
      this.columnApi.setColumnWidths(columnWidthsArray);
    }
  }

  async onFirstDataRendered($event: FirstDataRenderedEvent) {
    // Grid should be ready, resize columns.
    await this.resizeColumns();
  }

  /**
   * Handles grid model update events.
   */
  onModelUpdated($event) {
      if ($event.api.getDisplayedRowCount() === 0 && !this.isLoading) {
        $event.api.showNoRowsOverlay();
      }
      else {
        $event.api.hideOverlay();
      }
  }
}
